import type { Session, User } from "@supabase/supabase-js"
import { createContext, useContext, useEffect, useState } from "react"
import supabase, { getRedirectURL } from "../config/supabaseConfig"

interface AdminAuthContextType {
    admin: User | null
    session: Session | null
    isLoading: boolean
    error: string | null
    signIn: (email: string, password: string) => Promise<void>
    signInWithGoogle: () => Promise<void>
    signOut: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export const AdminAuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [admin, setAdmin] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let mounted = true;

        // Get initial session
        const initializeAuth = async () => {
            if (!mounted) return;

            setIsLoading(true);
            try {
                // Get the current session
                const { data } = await supabase.auth.getSession();

                if (mounted) {
                    if (data?.session) {
                        setAdmin(data.session.user);
                        setSession(data.session);
                    } else {
                        setAdmin(null);
                        setSession(null);
                    }

                    setIsLoading(false);
                }
            } catch {
                /* Removed console statement */
                if (mounted) {
                    setAdmin(null);
                    setSession(null);
                    setIsLoading(false);
                }
            }
        };

        initializeAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (_event, newSession) => {
                if (!mounted) return;

                if (newSession) {
                    setAdmin(newSession.user);
                    setSession(newSession);
                } else {
                    setAdmin(null);
                    setSession(null);
                }

                setIsLoading(false);
            }
        );

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const SignInWithGoogle = async () => {
        try {
            setError(null);
            setIsLoading(true);

            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${getRedirectURL()}admin-dashboard`,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    },
                }
            });

            if (error) {
                setError(error.message);
                return;
            }

        } catch {
            setError('An unexpected error occurred during Google sign-in');
            /* Removed console statement */
        } finally {
            setIsLoading(false);
        }
    };

    const SignIn = async (email: string, password: string) => {
        try {
            setError(null);
            setIsLoading(true);
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) {
                setError(error.message);
                throw error;
            }

            if (data?.user) {
                setAdmin(data.user);
                setSession(data.session);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'An unexpected error occurred';
            setError(message);
            throw err instanceof Error ? err : new Error(message);
        } finally {
            setIsLoading(false);
        }
    }

    const SignOut = async () => {
        try {
            setIsLoading(true);
            const { error } = await supabase.auth.signOut();

            if (error) {
                setError(error.message);
                return;
            }

            setAdmin(null);
            setSession(null);
        } catch {
            setError('An unexpected error occurred');
            /* Removed console statement */
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <AdminAuthContext.Provider value={{
            admin,
            session,
            isLoading,
            error,
            signIn: SignIn,
            signInWithGoogle: SignInWithGoogle,
            signOut: SignOut
        }}>
            {children}
        </AdminAuthContext.Provider>
    )
}

export const useAdminAuth = (): AdminAuthContextType => {
    const context = useContext(AdminAuthContext)
    if (!context) {
        throw new Error('useAdminAuth must be used within an AdminAuthProvider')
    }
    return context
} 